import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { utility } from '../utils/Utility';
import { environment } from '../../../environments/environment';
import {
  ApiResponse,
  TOTPDisableResponse,
  TOTPSetupConfirmResponse,
  TOTPSetupStartResponse,
  UsersFilter,
  UsersPagedResponse,
} from './utility-service.models';

@Injectable({
  providedIn: 'root',
})
export class ApiServiceService {
  private baseUrl = environment.server_url; // Change to your API base URL
  private loginUrl = environment.login_url; // Change to your API base URL

  suppressAlertError = false;

  constructor(private http: HttpClient) {}

  private withCredentials(options?: any) {
    return {
      ...(options ?? {}),
      withCredentials: true,
      headers:
        options?.headers instanceof HttpHeaders
          ? options.headers
          : options?.headers,
    };
  }

  public put(actionUrl: any, request: any, options?: any): Observable<any> {
    const url = this.baseUrl + actionUrl;

    return this.http
      .put<any>(url, request, this.withCredentials(options))
      .pipe(
        map((response) => response),
        catchError((error) => this.handleError(error)),
      );
  }

  public patch(actionUrl: any, request: any, options?: any): Observable<any> {
    const url = this.baseUrl + actionUrl;

    return this.http
      .patch<any>(url, request, this.withCredentials(options))
      .pipe(
        map((response) => response),
        catchError((error) => this.handleError(error)),
      );
  }

  public post(actionUrl: any, request: any, options?: any): Observable<any> {
    const url = this.baseUrl + actionUrl;

    return this.http
      .post<any>(url, request, this.withCredentials(options))
      .pipe(
        map((response) => response),
        catchError((error) => this.handleError(error)),
      );
  }

  public get<T = any>(actionUrl: any, options?: any): Observable<T | HttpEvent<T>> {
    const url = this.baseUrl + actionUrl;

    return this.http.get<T>(url, this.withCredentials(options)).pipe(
      map((response) => response),
      catchError((error) => this.handleError(error)),
    );
  }

  public delete(actionUrl: any, options?: any): Observable<any> {
    const url = this.baseUrl + actionUrl;

    return this.http.delete<any>(url, this.withCredentials(options)).pipe(
      map((response) => response),
      catchError((error) => this.handleError(error)),
    );
  }

  public get_Two_Params(actionUrl: any): Observable<any> {
    const url = this.baseUrl + actionUrl;

    return this.http.get<any>(url, this.withCredentials()).pipe(
      map((response) => response),
      catchError((error) => this.handleError(error)),
    );
  }

  handleError(error: HttpErrorResponse) {
    if (this.suppressAlertError) {
      return throwError(() => error);
    }

    const errorMessage = this.getReadableErrorMessage(error);
    return throwError(() => errorMessage);
  }

  private getReadableErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return 'Unable to reach the server right now. Check your connection and try again.';
    }

    const backendMessage =
      (typeof error.error === 'string' && error.error) ||
      error.error?.message ||
      error.error?.error ||
      error.error?.title ||
      '';

    if (error.status === 400) {
      return (
        backendMessage ||
        'The request could not be processed. Please review your input and try again.'
      );
    }

    if (error.status === 401 || error.status === 403) {
      return (
        backendMessage || 'The email or password you entered is incorrect.'
      );
    }

    if (error.status === 404) {
      return backendMessage || 'The requested service could not be found.';
    }

    if (error.status >= 500) {
      return (
        backendMessage ||
        'Something went wrong on the server. Please try again shortly.'
      );
    }

    return (
      backendMessage || 'We could not complete your request. Please try again.'
    );
  }

  public loginPost_(actionUrl: any, request: any): Observable<any> {
    return this.login(
      actionUrl,
      request,
      this.withCredentials({ headers: utility.allHeader }),
    );
  }

  public verifyTOTPLogin(request: {
    challenge_id: string;
    code: string;
  }): Observable<any> {
    return this.login(
      '/login/totp/verify',
      request,
      this.withCredentials({ headers: utility.allHeader }),
    );
  }

  public login(actionUrl: any, request: any, options?: any): Observable<any> {
    const url = this.loginUrl + actionUrl;

    return this.http
      .post<any>(url, request, this.withCredentials(options))
      .pipe(
        map((response) => response),
        catchError((error) => this.handleError(error)),
      );
  }

  public refreshToken(): Observable<any> {
    return this.login(
      '/refresh',
      {},
      this.withCredentials({ headers: utility.allHeader }),
    );
  }

  public logout(): Observable<any> {
    return this.login(
      '/logout',
      {},
      this.withCredentials({ headers: utility.allHeader }),
    );
  }

  public forgotPassword(request: any): Observable<any> {
    return this.login(
      '/forgot-password',
      request,
      this.withCredentials({ headers: utility.allHeader }),
    );
  }

  public startTOTPSetup(): Observable<ApiResponse<TOTPSetupStartResponse>> {
    return this.post('/users/me/2fa/totp/setup/start', {});
  }

  public confirmTOTPSetup(
    code: string,
  ): Observable<ApiResponse<TOTPSetupConfirmResponse>> {
    return this.post('/users/me/2fa/totp/setup/confirm', { code });
  }

  public disableTOTP(
    code: string,
  ): Observable<ApiResponse<TOTPDisableResponse>> {
    return this.post('/users/me/2fa/totp/disable', { code });
  }

  public getCurrentUser(): Observable<any> {
    return this.get('/me');
  }

  public getUsersPaged(
    filters: Partial<UsersFilter>,
    offset = 0,
    limit = 10,
  ): Observable<UsersPagedResponse> {
    const params = new HttpParams().set('offset', offset).set('limit', limit);

    return this.post('/users-paged', filters, { params });
  }

  public validateAsin(asin: string): Observable<any> {
    const baseUrl = this.baseUrl.replace(/\/$/, '');
    const apiBaseUrl = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
    const params = new HttpParams().set('asin', asin);

    return this.http
      .get<any>(
        `${apiBaseUrl}/utilities/validate-asin`,
        this.withCredentials({ params }),
      )
      .pipe(
        map((response) => response),
        catchError((error) => this.handleError(error)),
      );
  }

  public validateAsinWithDetails(asin: string): Observable<any> {
    const baseUrl = this.baseUrl.replace(/\/$/, '');
    const apiBaseUrl = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
    const params = new HttpParams().set('asin', asin);

    return this.http
      .get<any>(
        `${apiBaseUrl}/utilities/validate-asin/details`,
        this.withCredentials({ params }),
      )
      .pipe(
        map((response) => response),
        catchError((error) => this.handleError(error)),
      );
  }


  public validateBusinessRc(rcNumber: string, entityName: string): Observable<any> {
    const baseUrl = this.baseUrl.replace(/\/$/, '');

    return this.http
      .post<any>(
        `${baseUrl}/utility/validate-business-rc-name`,
        { rcNumber, entityName },
        this.withCredentials(),
      )
      .pipe(
        map((response) => response),
        catchError((error) => this.handleError(error)),
      );
  }

  public getVendorById(vendorId: string): Observable<any> {
    return this.get(`/vendors/${vendorId}`);
  }

  public getMyVendors(): Observable<any> {
    return this.get('/vendors/mine');
  }

  public getCertificateDownloadUrl(vendorId: string): string {
    const safeVendorId = encodeURIComponent(String(vendorId ?? '').trim());
    const baseUrl = this.baseUrl.replace(/\/$/, '');
    return `${baseUrl}/downloads/certificates/${safeVendorId}`;
  }

  public getReceiptDownloadUrl(vendorId: string): string {
    const safeVendorId = encodeURIComponent(String(vendorId ?? '').trim());
    const baseUrl = this.baseUrl.replace(/\/$/, '');
    return `${baseUrl}/downloads/receipts/${safeVendorId}`;
  }

  public getReceiptDownloadUrlByReference(paymentReference: string): string {
    const safePaymentReference = encodeURIComponent(String(paymentReference ?? '').trim());
    const baseUrl = this.baseUrl.replace(/\/$/, '');
    return `${baseUrl}/downloads/receipts/reference/${safePaymentReference}`;
  }

  public getDemandNoticeDownloadUrl(vendorId: string): string {
    const safeVendorId = encodeURIComponent(String(vendorId ?? '').trim());
    const baseUrl = this.baseUrl.replace(/\/$/, '');
    return `${baseUrl}/downloads/demand-notices/${safeVendorId}`;
  }

  public getAssessmentById(assessmentId: string): Observable<any> {
    return this.get(`/assessments/${assessmentId}`);
  }
}
